import { createChatCompletion } from './modelRouter';
import { type TaskType } from '../../config/openai/models';
import { getOptimalModel, getModelConfig } from '../../config/openai/models';

// Cache interface
interface CacheEntry {
  result: any;
  timestamp: number;
  expiryMs: number;
}

// Options interface for better type safety
export interface OptimizedCompletionOptions {
  useCache?: boolean;
  temperature?: number;
  max_tokens?: number;
  [key: string]: any;
}

// Cache configuration by task type
const CACHE_CONFIG: Record<TaskType, { ttl: number; enabled: boolean }> = {
  // Simple tasks - cache longer (5 minutes)
  'text-generation': { ttl: 300000, enabled: true },
  'basic-formatting': { ttl: 300000, enabled: true },
  'simple-qa': { ttl: 300000, enabled: true },
  
  // Standard tasks - cache moderately (2 minutes)
  'job-description': { ttl: 120000, enabled: true },
  'candidate-analysis': { ttl: 60000, enabled: false }, // Don't cache personal analysis
  'interview-questions': { ttl: 180000, enabled: true },
  'market-intelligence': { ttl: 180000, enabled: true },
  'auto-fill': { ttl: 120000, enabled: true },
  'audience-targeting': { ttl: 120000, enabled: true },
  
  // Complex tasks - cache briefly (1 minute)
  'bias-detection': { ttl: 60000, enabled: true },
  'complex-scoring': { ttl: 60000, enabled: false },
  'technical-analysis': { ttl: 60000, enabled: true },
  'skills-assessment': { ttl: 180000, enabled: true },
  'mathematical-analysis': { ttl: 60000, enabled: true },
  'strategic-analysis': { ttl: 60000, enabled: true }
};

class AISpeedOptimizer {
  private cache = new Map<string, CacheEntry>();
  private pendingRequests = new Map<string, Promise<any>>();

  /**
   * Generate cache key for request
   */
  private getCacheKey(taskType: TaskType, messages: any[], options: OptimizedCompletionOptions = {}): string {
    const messageContent = messages.map(m => m.content).join('|');
    const optionsKey = JSON.stringify(options);
    return `${taskType}:${this.hashString(messageContent + optionsKey)}`;
  }

  /**
   * Simple hash function for cache keys
   */
  private hashString(str: string): string {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
      const char = str.charCodeAt(i);
      hash = ((hash << 5) - hash) + char;
      hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash).toString(36);
  }

  /**
   * Check if cache entry is valid
   */
  private isCacheValid(entry: CacheEntry): boolean {
    return Date.now() - entry.timestamp < entry.expiryMs;
  }

  /**
   * Get cached result if available and valid
   */
  private getCachedResult(cacheKey: string): any | null {
    const entry = this.cache.get(cacheKey);
    if (entry && this.isCacheValid(entry)) {
      return entry.result;
    }
    
    // Clean up expired entry
    if (entry) {
      this.cache.delete(cacheKey);
    }
    
    return null;
  }

  /**
   * Store result in cache
   */
  private setCachedResult(cacheKey: string, result: any, ttl: number): void {
    this.cache.set(cacheKey, {
      result,
      timestamp: Date.now(),
      expiryMs: ttl
    });
  }

  /**
   * Clean up expired cache entries
   */
  private cleanupCache(): void {
    const now = Date.now();
    for (const [key, entry] of this.cache.entries()) {
      if (now - entry.timestamp >= entry.expiryMs) {
        this.cache.delete(key);
      }
    }
  }

  /**
   * Optimized AI completion with caching and deduplication
   * 
   * @param taskType - Type of task for optimal model selection
   * @param messages - Array of message objects for the completion
   * @param options - Options object (useCache will be handled internally)
   */
  async optimizedCompletion(
    taskType: TaskType,
    messages: any[],
    options: OptimizedCompletionOptions = {}
  ): Promise<any> {
    const config = CACHE_CONFIG[taskType];
    
    // Extract cache options (handled internally, not passed to API)
    const shouldUseCache = options.useCache !== false && config.enabled;
    
    const cacheKey = this.getCacheKey(taskType, messages, options);

    // Check cache first (if enabled for this task type)
    if (shouldUseCache) {
      const cachedResult = this.getCachedResult(cacheKey);
      if (cachedResult) {
        // Add small delay to simulate processing for better UX
        await new Promise(resolve => setTimeout(resolve, 100));
        return cachedResult;
      }
    }

    // Check if same request is already pending (deduplication)
    const pendingRequest = this.pendingRequests.get(cacheKey);
    if (pendingRequest) {
      return pendingRequest;
    }

    // Create new request
    const requestPromise = this.executeRequest(taskType, messages, options);
    this.pendingRequests.set(cacheKey, requestPromise);

    try {
      const result = await requestPromise;
      
      // Cache the result if caching is enabled
      if (shouldUseCache) {
        this.setCachedResult(cacheKey, result, config.ttl);
      }
      
      return result;
    } finally {
      // Clean up pending request
      this.pendingRequests.delete(cacheKey);
      
      // Periodic cache cleanup
      if (Math.random() < 0.1) { // 10% chance
        this.cleanupCache();
      }
    }
  }

  /**
   * Execute the actual AI request with optimizations
   */
  private async executeRequest(taskType: TaskType, messages: any[], options: OptimizedCompletionOptions): Promise<any> {
    // Optimize messages for speed
    const optimizedMessages = this.optimizeMessages(messages);
    
    // Optimize options for speed
    const optimizedOptions = this.optimizeOptions(taskType, options);
    
    const response = await createChatCompletion(taskType, optimizedMessages, optimizedOptions);
    return response;
  }

  /**
   * Optimize messages for faster processing
   */
  private optimizeMessages(messages: any[]): any[] {
    return messages.map(message => ({
      ...message,
      content: this.optimizeContent(message.content)
    }));
  }

  /**
   * Optimize content for faster processing
   */
  private optimizeContent(content: string): string {
    // Remove excessive whitespace
    content = content.replace(/\s+/g, ' ').trim();
    
    // Truncate extremely long content (keep most important parts)
    if (content.length > 8000) {
      // Keep beginning and end, truncate middle
      const start = content.substring(0, 4000);
      const end = content.substring(content.length - 2000);
      content = start + '\n\n[... content truncated for processing speed ...]\n\n' + end;
    }
    
    return content;
  }

  /**
   * Optimize options for speed
   */
  private optimizeOptions(taskType: TaskType, options: OptimizedCompletionOptions): OptimizedCompletionOptions {
    const optimized = { ...options };
    
    // Remove custom parameters that shouldn't go to OpenAI API
    delete optimized.useCache;
    delete optimized.taskType;
    
    // Get the optimal model and its configuration for proper parameter handling
    const model = getOptimalModel(taskType);
    const modelConfig = getModelConfig(model);
    
    // Set optimized max_tokens for faster responses where appropriate
    let maxTokens = optimized.max_tokens;
    if (!maxTokens) {
      switch (taskType) {
        case 'text-generation':
        case 'basic-formatting':
        case 'simple-qa':
          maxTokens = 500; // Shorter responses for simple tasks
          break;
        case 'auto-fill':
          maxTokens = 800; // Medium responses
          break;
        case 'audience-targeting':
          maxTokens = 4000; // Large responses for full job description optimization
          break;
        default:
          maxTokens = 1500; // Default for complex tasks
      }
    }
    
    // Handle different token parameter names for different models
    // This logic matches what's in modelRouter.ts
    if ((modelConfig as any).useMaxCompletionTokens) {
      optimized.max_completion_tokens = maxTokens;
      delete optimized.max_tokens; // Remove the unsupported parameter
    } else {
      optimized.max_tokens = maxTokens;
    }
    
    // Handle temperature parameter - only set if supported by the model
    if (!(modelConfig as any).disableTemperature) {
      // Optimize temperature for speed vs quality trade-off
      if (optimized.temperature === undefined) {
        switch (taskType) {
          case 'text-generation':
          case 'basic-formatting':
            optimized.temperature = 0.3; // Lower temperature for faster, more predictable responses
            break;
          case 'bias-detection':
          case 'technical-analysis':
            optimized.temperature = 0.1; // Very low for analytical tasks
            break;
          default:
            optimized.temperature = 0.5; // Balanced for other tasks
        }
      }
    } else {
      // Remove temperature parameter if not supported by this model
      delete optimized.temperature;
    }
    
    // Handle penalty parameters - only set if supported by the model
    if ((modelConfig as any).disablePenalties) {
      delete optimized.presence_penalty;
      delete optimized.frequency_penalty;
    }
    
    return optimized;
  }

  /**
   * Batch multiple requests for efficiency
   */
  async batchRequests(requests: Array<{
    taskType: TaskType;
    messages: any[];
    options?: OptimizedCompletionOptions;
  }>): Promise<any[]> {
    // Execute requests in parallel with concurrency limit
    const BATCH_SIZE = 3; // Process 3 requests at a time to avoid rate limits
    const results: any[] = [];
    
    for (let i = 0; i < requests.length; i += BATCH_SIZE) {
      const batch = requests.slice(i, i + BATCH_SIZE);
      const batchPromises = batch.map(request => 
        this.optimizedCompletion(request.taskType, request.messages, request.options)
      );
      
      const batchResults = await Promise.all(batchPromises);
      results.push(...batchResults);
    }
    
    return results;
  }

  /**
   * Prefetch common requests to warm cache
   */
  async prefetchCommonRequests(): Promise<void> {
    const commonRequests = [
      {
        taskType: 'auto-fill' as TaskType,
        messages: [
          { role: 'system', content: 'You are a helpful assistant for job descriptions.' },
          { role: 'user', content: 'Auto-fill suggestions for Software Engineer at Tech Company' }
        ]
      },
      {
        taskType: 'skills-assessment' as TaskType,
        messages: [
          { role: 'system', content: 'Generate technical assessment questions.' },
          { role: 'user', content: 'Create JavaScript assessment questions' }
        ]
      }
    ];

    // Execute prefetch requests in background
    Promise.all(
      commonRequests.map(request => 
        this.optimizedCompletion(request.taskType, request.messages).catch(() => {
          // Ignore prefetch errors
        })
      )
    );
  }

  /**
   * Debug method to show parameter filtering for a given task type
   * Useful for troubleshooting API parameter issues
   */
  debugParameterFiltering(taskType: TaskType, options: OptimizedCompletionOptions = {}): {
    model: string;
    filteredParameters: string[];
    finalOptions: OptimizedCompletionOptions;
  } {
    const model = getOptimalModel(taskType);
    const modelConfig = getModelConfig(model);
    const filteredParameters: string[] = [];
    
    // Simulate the parameter filtering process
    const testOptions = { ...options, useCache: true, temperature: 0.7, max_tokens: 1000 };
    
    if (testOptions.useCache) filteredParameters.push('useCache (custom parameter)');
    if ((modelConfig as any).disableTemperature) filteredParameters.push('temperature (unsupported by model)');
    if ((modelConfig as any).disablePenalties) {
      filteredParameters.push('presence_penalty (unsupported by model)');
      filteredParameters.push('frequency_penalty (unsupported by model)');
    }
    if ((modelConfig as any).useMaxCompletionTokens) {
      filteredParameters.push('max_tokens → max_completion_tokens (parameter mapping)');
    }
    
    const finalOptions = this.optimizeOptions(taskType, testOptions);
    
    return {
      model,
      filteredParameters,
      finalOptions
    };
  }

  /**
   * Get cache statistics
   */
  getCacheStats(): {
    size: number;
    hitRate: number;
    totalRequests: number;
  } {
    return {
      size: this.cache.size,
      hitRate: 0, // Would need to track hits/misses for accurate calculation
      totalRequests: this.pendingRequests.size
    };
  }

  /**
   * Clear cache manually
   */
  clearCache(): void {
    this.cache.clear();
  }
}

// Export singleton instance
export const aiSpeedOptimizer = new AISpeedOptimizer();

// Export convenience function
export const optimizedCompletion = aiSpeedOptimizer.optimizedCompletion.bind(aiSpeedOptimizer);
export const batchRequests = aiSpeedOptimizer.batchRequests.bind(aiSpeedOptimizer);
export const prefetchCommon = aiSpeedOptimizer.prefetchCommonRequests.bind(aiSpeedOptimizer);
export const debugParameterFiltering = aiSpeedOptimizer.debugParameterFiltering.bind(aiSpeedOptimizer); 